import React, { useEffect, useState, useCallback, useMemo } from "react";
import { FaSearch } from "react-icons/fa";
import { apiFetch } from "./utils/api";
import dnPopup from "./utils/dnPopup";
import logo_eagle from "./assets/img/logo_eagle.png";
import "./Timesheet_approvals.css";

const DAY_LABELS = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];

// Format a Monday week_start (YYYY-MM-DD) as "06 Jul - 12 Jul, 2026"
const weekLabel = (weekStartYMD) => {
    if (!weekStartYMD) return "";
    const start = new Date(`${weekStartYMD}T00:00:00`);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    const d = (x) => x.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
    return `${d(start)} - ${d(end)}, ${end.getFullYear()}`;
};

const num = (v) => Number(v) || 0;
const fmt = (v) => num(v).toFixed(2);
const rowTotal = (r) =>
    num(r.mon_hours) + num(r.tue_hours) + num(r.wed_hours) + num(r.thu_hours) +
    num(r.fri_hours) + num(r.sat_hours) + num(r.sun_hours);
const dayVals = (r) => [r.mon_hours, r.tue_hours, r.wed_hours, r.thu_hours, r.fri_hours, r.sat_hours, r.sun_hours];

const Timesheet_approvals = ({ navSize }) => {
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");

    // detail modal
    const [detailFor, setDetailFor] = useState(null); // the approval row
    const [detailRows, setDetailRows] = useState([]);
    const [detailLoading, setDetailLoading] = useState(false);

    // reject modal
    const [rejectFor, setRejectFor] = useState(null);
    const [rejectReason, setRejectReason] = useState("");

    const [busyKey, setBusyKey] = useState(null); // emp_id|week_start being acted on

    const keyOf = (row) => `${row.emp_id}|${row.week_start}`;

    const fetchApprovals = useCallback(async () => {
        setLoading(true);
        try {
            const res = await apiFetch("/api/timesheet/approvals", { credentials: "include" });
            const data = await res.json();
            setList(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("Error fetching timesheet approvals:", err);
            setList([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchApprovals(); }, [fetchApprovals]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return list;
        return list.filter((r) =>
            `${r.emp_name || ""} ${r.emp_id || ""} ${r.department_name || ""}`.toLowerCase().includes(q)
        );
    }, [list, search]);

    const openDetail = async (row) => {
        setDetailFor(row);
        setDetailRows([]);
        setDetailLoading(true);
        try {
            const res = await apiFetch(
                `/api/timesheet/approval_detail?emp_id=${encodeURIComponent(row.emp_id)}&week_start=${encodeURIComponent(row.week_start)}`,
                { credentials: "include" }
            );
            const data = await res.json();
            setDetailRows(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("Error fetching timesheet detail:", err);
        } finally {
            setDetailLoading(false);
        }
    };

    const updateStatus = async (row, status, reason = null) => {
        setBusyKey(keyOf(row));
        try {
            const res = await apiFetch("/api/timesheet/approval_status", {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ emp_id: row.emp_id, week_start: row.week_start, status, reason }),
                credentials: "include",
            });
            const data = await res.json();
            if (data.success) {
                dnPopup.success(`Timesheet ${status}`, `${row.emp_name}'s timesheet has been ${status.toLowerCase()}.`);
                await fetchApprovals();
            } else {
                dnPopup.error("Action Failed", data.message || "Something went wrong.");
            }
        } catch (err) {
            console.error("Error updating timesheet status:", err);
            dnPopup.error("Error", "Failed to update timesheet status.");
        } finally {
            setBusyKey(null);
        }
    };

    const approve = async (row) => {
        const ok = await dnPopup.confirm({
            title: "Approve timesheet?",
            message: `Approve ${row.emp_name}'s timesheet for ${weekLabel(row.week_start)}?`,
            confirmLabel: "Yes, Approve",
        });
        if (ok) { setDetailFor(null); updateStatus(row, "Approved"); }
    };

    const openReject = (row) => {
        setRejectFor(row);
        setRejectReason("");
    };

    const confirmReject = () => {
        if (!rejectReason.trim()) {
            dnPopup.warning("Reason required", "Please enter a reason before rejecting.");
            return;
        }
        const row = rejectFor;
        setRejectFor(null);
        setDetailFor(null);
        updateStatus(row, "Rejected", rejectReason.trim());
    };

    const workRows = detailRows.filter((r) => r.entry_type !== "timeoff");
    const timeOffRow = detailRows.find((r) => r.entry_type === "timeoff");
    const grandTotal = workRows.reduce((a, r) => a + rowTotal(r), 0) + (timeOffRow ? rowTotal(timeOffRow) : 0);
    const billableTotal = workRows.filter((r) => r.client_billable === "Billable").reduce((a, r) => a + rowTotal(r), 0);
    const nonBillableTotal = workRows.filter((r) => r.client_billable !== "Billable").reduce((a, r) => a + rowTotal(r), 0);

    // Flag a day red when work + leave/holiday exceeds what the day allows:
    //  - partial leave/holiday day (0 < off < 8h): total must stay within 8h
    //  - normal day: overtime allowed, so only impossible totals (> 24h) flag
    const STANDARD_DAY = 8;
    const dayColumnTotal = (i) => detailRows.reduce((a, r) => a + num(dayVals(r)[i]), 0);
    const dayOverLimit = (i) => {
        const off = timeOffRow ? num(dayVals(timeOffRow)[i]) : 0;
        const total = dayColumnTotal(i);
        if (off > 0 && off < STANDARD_DAY) return total > STANDARD_DAY;
        return total > 24;
    };
    const hasOverLimit = DAY_LABELS.some((_, i) => dayOverLimit(i));

    const busyDetail = detailFor && busyKey === keyOf(detailFor);

    return (
        <div className="ts-appr-container">
            <div className={`ts-appr-dashboard ${navSize}`}>
                <div className="ts-appr-header">
                    <h2>
                        <a className="navbar-brand" href="#">
                            <img src={logo_eagle} alt="dTime Logo" className="page-logo" />
                        </a>
                        Timesheet Approvals
                    </h2>
                    <p className="ts-appr-subtitle">
                        Review, approve, or reject employee timesheets submitted for the week.
                        Reject requires a reason; an email is sent to the employee on decision.
                    </p>
                </div>

                {/* Toolbar: search + submitted count */}
                <div className="ts-appr-toolbar">
                    <div className="ts-appr-search">
                        <FaSearch className="ts-appr-search-icon" />
                        <input
                            type="text"
                            placeholder="Search by name, employee ID, or department..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                    <div className="ts-appr-count">{filtered.length} submitted</div>
                </div>

                <div className="ts-appr-card">
                    {loading ? (
                        <div className="ts-appr-empty">Loading…</div>
                    ) : filtered.length === 0 ? (
                        <div className="ts-appr-empty">No timesheets are waiting for your approval.</div>
                    ) : (
                        <table className="ts-appr-table">
                            <thead>
                                <tr>
                                    <th>Employee</th>
                                    <th>Employee ID</th>
                                    <th>Department</th>
                                    <th>Week</th>
                                    <th>Submitted</th>
                                    <th>Total Hours</th>
                                    <th>Status</th>
                                    <th className="ts-appr-actions-col">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((row) => (
                                    <tr key={keyOf(row)}>
                                        <td className="ts-appr-empname">{row.emp_name || row.emp_id}</td>
                                        <td><span className="ts-appr-idchip">{row.emp_id}</span></td>
                                        <td>{row.department_name || "—"}</td>
                                        <td>{weekLabel(row.week_start)}</td>
                                        <td>{row.submitted_time || "—"}</td>
                                        <td className="ts-appr-hours">{fmt(row.total_hours)}</td>
                                        <td>
                                            <span className={`ts-appr-pill ts-appr-pill-${(row.status || "Submitted").toLowerCase()}`}>
                                                {row.status || "Submitted"}
                                            </span>
                                        </td>
                                        <td className="ts-appr-actions-col">
                                            <button className="ts-appr-btn ts-appr-review" onClick={() => openDetail(row)}>
                                                Review
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Review modal — approve / reject happen here */}
            {detailFor && (
                <div className="ts-appr-modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setDetailFor(null); }}>
                    <div className="ts-appr-modal">
                        <div className="ts-appr-modal-header">
                            <div>
                                <div className="ts-appr-modal-title">Review Timesheet</div>
                                <div className="ts-appr-modal-meta">
                                    <span className="ts-appr-modal-name">{detailFor.emp_name || detailFor.emp_id}</span>
                                    <span className="ts-appr-idchip">{detailFor.emp_id}</span>
                                    {detailFor.department_name && <span className="ts-appr-meta-dot">· {detailFor.department_name}</span>}
                                    <span className="ts-appr-meta-dot">· {weekLabel(detailFor.week_start)}</span>
                                </div>
                            </div>
                            <button className="ts-appr-modal-close" onClick={() => setDetailFor(null)}>✕</button>
                        </div>

                        <div className="ts-appr-modal-body">
                            {detailLoading ? (
                                <div className="ts-appr-empty">Loading…</div>
                            ) : detailRows.length === 0 ? (
                                <div className="ts-appr-empty">No entries found.</div>
                            ) : (
                                <div className="ts-appr-detail-scroll">
                                    {hasOverLimit && (
                                        <div className="ts-appr-warn">
                                            <strong>Hours in red exceed the allowed limit:</strong> on those days the
                                            work hours plus leave/holiday go over what the day permits
                                            (a half-day leave day allows only 4h of work). Please review before approving.
                                        </div>
                                    )}
                                    <table className="ts-appr-detail-table">
                                        <thead>
                                            <tr>
                                                <th>Type</th>
                                                <th>Project</th>
                                                <th>Task</th>
                                                <th>Onsite/Offshore</th>
                                                <th>Billable</th>
                                                <th>Location</th>
                                                {DAY_LABELS.map((d, i) => (
                                                    <th key={d} className={`ts-appr-day${dayOverLimit(i) ? " ts-appr-over" : ""}`}>{d}</th>
                                                ))}
                                                <th className="ts-appr-day">Total</th>
                                                <th>Comments</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {workRows.map((r, idx) => (
                                                <tr key={idx}>
                                                    <td>{r.entry_type || "work"}</td>
                                                    <td>
                                                        <div className="ts-appr-projname">{r.project_name || "—"}</div>
                                                        {r.project_id && <div className="ts-appr-projid">{r.project_id}</div>}
                                                    </td>
                                                    <td>{r.task_id || "—"}</td>
                                                    <td>{r.onsite_offshore || "—"}</td>
                                                    <td>{r.client_billable || "—"}</td>
                                                    <td>{r.billing_location || "—"}</td>
                                                    {dayVals(r).map((h, i) => (
                                                        <td key={i} className={`ts-appr-day${dayOverLimit(i) ? " ts-appr-over" : ""}`}>{fmt(h)}</td>
                                                    ))}
                                                    <td className="ts-appr-day ts-appr-rowtotal">{fmt(rowTotal(r))}</td>
                                                    <td className="ts-appr-comments">{r.comments || "—"}</td>
                                                </tr>
                                            ))}
                                            {timeOffRow && (
                                                <tr className="ts-appr-timeoff">
                                                    <td>{timeOffRow.entry_type || "timeoff"}</td>
                                                    <td>—</td>
                                                    <td>—</td>
                                                    <td>—</td>
                                                    <td>—</td>
                                                    <td>—</td>
                                                    {dayVals(timeOffRow).map((h, i) => (
                                                        <td key={i} className={`ts-appr-day${dayOverLimit(i) ? " ts-appr-over" : ""}`}>{fmt(h)}</td>
                                                    ))}
                                                    <td className="ts-appr-day ts-appr-rowtotal">{fmt(rowTotal(timeOffRow))}</td>
                                                    <td className="ts-appr-comments">—</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>

                        <div className="ts-appr-modal-footer">
                            <div className="ts-appr-footer-summary">
                                <div className="ts-appr-sumitem">
                                    <span className="ts-appr-sumlabel">Billable</span>
                                    <span className="ts-appr-sumval">{fmt(billableTotal)}</span>
                                </div>
                                <div className="ts-appr-sumitem">
                                    <span className="ts-appr-sumlabel">Non-Billable</span>
                                    <span className="ts-appr-sumval">{fmt(nonBillableTotal)}</span>
                                </div>
                                <div className="ts-appr-sumitem">
                                    <span className="ts-appr-sumlabel">Grand Total</span>
                                    <span className="ts-appr-sumval ts-appr-sumval-total">{fmt(grandTotal)}</span>
                                </div>
                            </div>
                            <div className="ts-appr-footer-actions">
                                <button className="ts-appr-btn ts-appr-reject" disabled={busyDetail} onClick={() => openReject(detailFor)}>
                                    Reject
                                </button>
                                <button className="ts-appr-btn ts-appr-approve" disabled={busyDetail} onClick={() => approve(detailFor)}>
                                    Approve
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Reject reason modal */}
            {rejectFor && (
                <div className="ts-appr-modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setRejectFor(null); }}>
                    <div className="ts-appr-modal ts-appr-modal-sm">
                        <div className="ts-appr-modal-header">
                            <div className="ts-appr-modal-title">Reason for Rejection</div>
                            <button className="ts-appr-modal-close" onClick={() => setRejectFor(null)}>✕</button>
                        </div>
                        <div className="ts-appr-modal-body">
                            <textarea
                                className="ts-appr-textarea"
                                rows={4}
                                value={rejectReason}
                                placeholder={`Why is ${rejectFor.emp_name || "this"}'s timesheet being rejected?`}
                                onChange={(e) => setRejectReason(e.target.value)}
                            />
                        </div>
                        <div className="ts-appr-modal-footer ts-appr-modal-footer-simple">
                            <button className="ts-appr-btn ts-appr-cancel" onClick={() => setRejectFor(null)}>Cancel</button>
                            <button className="ts-appr-btn ts-appr-reject" onClick={confirmReject}>Reject Timesheet</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Timesheet_approvals;
